import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { navigate } from 'gatsby';
import { RRPCART } from '../../constants';
import { rrpModal } from '../../config/notifications';

import { ShoppingCart as RRPShoppingCartModule } from '@common-modules/shopping-cart';
import RRPModal from './RRPModal';
import { ACTION_TYPES } from '../../constants/actions';
import { useDispatch } from 'react-redux';

import {
  getShoppingCartDataModel,
  getProductOrderItem,
  flattenRRPProductsList,
  getCatalogPath
} from '../../helpers/rrpCart';
import { dataLayerPush, flattenNodes } from '../../helpers/common';
import { trans as t } from '../../helpers/localisation';

import { rrpCartActions, cartActions } from '@detox/actions';

import GenericError from '../ShoppingCart/GenericError';
import { getCartErrorDataModel } from '../../helpers/cart';
import { navigation } from '../../middlewares/navigation-constants';
import { rrpCheckoutDataLayer } from '../../helpers/dataLayerHelpers';
import { useLoader } from '../../hooks/useLoader';
import { SkeletonType } from '../SkeletonLoader';
import { getAccessoryOnlineStock } from '../../helpers/accessory';

export const RRPShoppingCart = ({
  data,
  rrpOrder,
  addedItems,
  removedItems,
  rrpCart,
  retrieveRrpMasterOrderDetails,
  rrpAddMasterOrderItems,
  rrpRemoveMasterOrderItems,
  getShoppingCartData,
  mtposStock,
  mtposLoading,
  rrpCancelMasterOrderItems,
  updateRrpMasterOrderID
}) => {
  const dispatch = useDispatch();
  const allRrpCart = flattenNodes(data.allRrpCart);
  const allRrpProduct = flattenNodes(data.allRrpProduct);
  const [rrpMasterOrderId, setRrpMasterOrderId] = useState('');
  const quantityLimit = allRrpCart?.find(obj => obj.type === 'quantity');
  const amountLimit = allRrpCart?.find(obj => obj.type === 'amount');
  const isQuantityExceeded = useMemo(() => {
    const totalQuantity = rrpOrder?.ImplRRPOrderDetails?.reduce(
      (acc, orderDetails) => {
        const { orderItems } = orderDetails;
        orderItems.forEach(orderItem => {
          acc += orderItem.quantity;
        });
        return acc;
      },
      0
    );

    return totalQuantity >= quantityLimit?.maxLimit;
  }, [rrpOrder, quantityLimit]);
  const isAmountExceeded =
    rrpOrder?.actualPrice?.afterTax > amountLimit?.maxLimit;
  const canIncreaseQuantity = !isAmountExceeded && !isQuantityExceeded;

  const variantBySku = useMemo(() => {
    return allRrpProduct.reduce((acc, cur) => {
      cur.variants.forEach(variant => {
        const { sku, ...data } = variant;
        if (!(sku in acc)) {
          acc[sku] = {
            ...data,
            productId: cur.id,
            maxAllowedQuantity: cur.maxAllowedQuantity
          };
        }
      });
      return acc;
    }, {});
  }, [allRrpProduct]);

  const [openModal, setOpenModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [shoppingCartDataModel, setShoppingCartDataModel] = useState(null);
  const [checkoutFailed, setCheckoutFailed] = useState(false);
  const [rrpProductList, setRrpProductList] = useState();

  const getMasterOrderDetails = async rrpMasterOrderIdFromCache => {
    setRrpMasterOrderId(rrpMasterOrderIdFromCache);
    await retrieveRrpMasterOrderDetails({
      rrpMasterOrderId: rrpMasterOrderIdFromCache
    });
    updateRrpMasterOrderID({
      rrpMasterOrderId: rrpMasterOrderIdFromCache
    });

    const mtposResponse = new Map((mtposStock ?? []).map(m => [m.itemCode, m]));
    const uniqueSkus = [...new Set((addedItems ?? []).map(i => i.sku))];

    const result = uniqueSkus.flatMap(sku => {
      const match = mtposResponse.get(sku);
      if (match) {
        return [{
          allocationID: match.allocationID,
          status: match.status,
          availableQty: match.availableQty,
          itemCode: match.itemCode
        }];
      } else {
        // Find the addedItem for this sku
        const addedItem = (addedItems ?? []).find(i => i.sku === sku);
        if (addedItem) {
          return [{
            allocationID: addedItem.mtposReservationId,
            status: "AL",
            availableQty: addedItem.quantity,
            itemCode: addedItem.sku
          }];
        }
        return [];
      }
    });

    dispatch({
      type: ACTION_TYPES.FULFILMENT.GET_MTPOS_SUCCESS,
      payload: result
    });
  };

  const { Loader } = useLoader({
    options: { type: SkeletonType.sideBar },
    loadings: [rrpCart?.loading],
    skeletonLoadings: [mtposLoading, rrpCart?.skeletonLoading]
  });

  useEffect(() => {
    if (rrpCart?.masterOrderId) {
      getMasterOrderDetails(rrpCart.masterOrderId);
    } else {
      navigate('/empty-shopping-cart', {
        state: {
          shopUrl: getCatalogPath()
        }
      });
    }
  }, []);

  useEffect(() => {
    if (!rrpCart?.rrpOrder && rrpCart?.finishCancelMasterOrder) {
      dispatch({ type: ACTION_TYPES.RRP.RRP_CLEAR_ORDER_DATA });
    }
  }, [rrpCart?.finishCancelMasterOrder]);

  useEffect(() => {
    if (mtposStock && rrpOrder) {
      const modalData = rrpOrder;

      const { itemQuantity, ...dataModal } = getShoppingCartDataModel(
        modalData,
        mtposStock,
        variantBySku,
        canIncreaseQuantity
      );
      setShoppingCartDataModel(dataModal);
    }
  }, [mtposStock, rrpOrder, canIncreaseQuantity]);

  useEffect(() => {
    if (rrpOrder?.ImplRRPOrderDetails) {
      const orderItems = flattenRRPProductsList(rrpOrder.ImplRRPOrderDetails);
      if (orderItems && orderItems.length > 0) {
        setRrpProductList(orderItems);
        dispatch({ type: ACTION_TYPES.RRP.SET_RRP_FLOW });
      } else {
        setCheckoutFailed(true);
      }
    }
  }, [rrpOrder?.ImplRRPOrderDetails]);

  const handleChangeQuantity = (value, item) => {
    const isIncreasing = value > item.quantity.value;

    const orderItem = getProductOrderItem(rrpProductList, item.sku);

    if (isIncreasing) {
      rrpAddMasterOrderItems({
        rrpMasterOrderId,
        orderItemDetails: [
          {
            sku: orderItem.sku,
            identifier: orderItem.identifier,
            productDetails: orderItem.productDetails
          }
        ]
      });
    } else {
      rrpRemoveMasterOrderItems({
        rrpMasterOrderId,
        orderId: orderItem.orderId,
        orderItemDetails: [{ orderItemId: orderItem.orderItemIds[0] }]
      });
    }
  };

  const handleRemoveAllItems = selectedOrderItem => {
    const orderItem = getProductOrderItem(
      rrpProductList,
      selectedOrderItem.sku
    );
    const orderItemDetails = orderItem.orderItemIds.map(id => ({
      orderItemId: id
    }));
    if (orderItem.isOnlyProductInOrder && orderItem.isCancelMasterOrder) {
      rrpCancelMasterOrderItems({
        rrpMasterOrderId
      });
    } else {
      rrpRemoveMasterOrderItems({
        rrpMasterOrderId,
        orderId: orderItem.orderId,
        orderItemDetails,
        cancelOrder: orderItem.isOnlyProductInOrder
      });
    }
  };

  const handleCheckout = async () => {
    if (isAmountExceeded) {
      setOpenModal(true);
      setModalType(RRPCART.MODAL_TYPE.EXCEED_AMOUNT);
    } else {
      if (rrpOrder?.ImplRRPOrderDetails) {
        const orderItems = flattenRRPProductsList(rrpOrder.ImplRRPOrderDetails);
        if (orderItems && orderItems.length > 0) {
          dataLayerPush(
            rrpCheckoutDataLayer({
              baseParams: {
                cartItems: rrpCart?.addedItems,
                location: window.location
              }
            })
          );
          navigate(`/${navigation.CUSTOMER_REGISTRATION}`);
        } else {
          handleEmptyCart();
        }
      }
    }
  };

  const handleLink = () => {
    navigate(`/${navigation.RRP_PRODUCT_CATALOG}`);
  };

  const handleEmptyCart = () => {
    setOpenModal(true);
    setModalType(RRPCART.MODAL_TYPE.EMPTY_CART);
  };

  const handleConfirmEmptyCart = async () => {
    setOpenModal(false);
    await rrpCancelMasterOrderItems({
      rrpMasterOrderId
    });
  };

  const renderModal = type => {
    switch (type) {
      case RRPCART.MODAL_TYPE.EMPTY_CART:
        return (
          <RRPModal
            header={rrpModal?.[type]?.title}
            message={rrpModal?.[type]?.body}
            ctaText={rrpModal?.[type]?.ctaText}
            visible={openModal}
            onClose={() => setOpenModal(false)}
            onButtonClick={() => handleConfirmEmptyCart()}
          />
        );

      case RRPCART.MODAL_TYPE.SESSION_TIMEOUT:
        return (
          <RRPModal
            header={rrpModal?.[type]?.title}
            message={rrpModal?.[type]?.body}
            ctaText={rrpModal?.[type]?.ctaText}
            visible={openModal}
            onClose={() => setOpenModal(false)}
          />
        );

      case RRPCART.MODAL_TYPE.SESSION_EXPIRED:
        return (
          <RRPModal
            header={rrpModal?.[type]?.title}
            message={rrpModal?.[type]?.body}
            ctaText={rrpModal?.[type]?.ctaText}
            visible={openModal}
            onClose={() => setOpenModal(false)}
            secondary={true}
          />
        );

      case RRPCART.MODAL_TYPE.EXCEED_AMOUNT:
        return (
          <RRPModal
            header={amountLimit.popupTitle}
            visible={openModal}
            message={amountLimit.popupDescription}
            onClose={() => setOpenModal(false)}
            onButtonClick={() => setOpenModal(false)}
            ctaText={amountLimit.popupCTALabel}
            secondary={true}
          />
        );

      default:
        return null;
    }
  };

  const renderCartErrors = hasOutOfStock => {
    const FALLBACK_ERROR = {
      title: 'Sorry, something went wrong',
      errorText:
        'It seems like we have encountered an error and are unable to proceed. Please try again.'
    };
    const cartErrorDataModel = getCartErrorDataModel(FALLBACK_ERROR, null);

    return (
      <GenericError
        title={
          hasOutOfStock
            ? t('DROP_SHIPPING.IPP.NO_LONGER_AVAILABLE_ITEM_TITLE')
            : cartErrorDataModel.title
        }
        errorText={
          hasOutOfStock
            ? t('DROP_SHIPPING.IPP.NO_LONGER_AVAILABLE_ITEM_MSG')
            : cartErrorDataModel.errorText
        }
        secondaryButtonText={
          hasOutOfStock
            ? t('DROP_SHIPPING.IPP.START_SHOPPING')
            : t('DROP_SHIPPING.BACK_TO_PRODUCT_CATALOGUE.CTA')
        }
        onSecondaryClick={() => navigate(getCatalogPath())}
      />
    );
  };

  if (shoppingCartDataModel && !rrpOrder?.ImplRRPOrderDetails?.length) {
    navigate('/empty-shopping-cart', {
      state: {
        shopUrl: getCatalogPath()
      }
    });
    return null;
  } else if (
    shoppingCartDataModel?.outOfStockItem?.dataSource?.length > 0 ||
    rrpCart?.error
  ) {
    return renderCartErrors(
      shoppingCartDataModel?.outOfStockItem?.dataSource?.length > 0
    );
  } else {
    return (
      <>
        <div data-testid="rrp-shopping-cart-module">
          <Loader>
            {checkoutFailed
              ? renderCartErrors(true)
              : rrpMasterOrderId &&
                shoppingCartDataModel && (
                  <RRPShoppingCartModule
                    data={shoppingCartDataModel}
                    callbacks={{
                      onChangeQuantity: handleChangeQuantity,
                      onChangePhone: () => console.log('changephone'),
                      onChangePlan: () => console.log('changeplan'),
                      onChangeNumber: () => console.log('changenumber'),
                      onAddItem: null,
                      onRemoveItem: handleRemoveAllItems,
                      onViewAvailableProduct: () =>
                        console.log('navigate to ViewAvailableProduct'),
                      onCheckout: handleCheckout,
                      onLink: handleLink,
                      onEmptyCart: handleEmptyCart
                    }}
                    configs={{
                      type:
                        'rrp' in shoppingCartDataModel
                          ? 'RRP_FULLPRICE'
                          : 'RRP_IPP',
                      loading: false,
                      deviceOutOfStock: false,
                      showTradeIn: false,
                      showVoucher: false,
                      alert: {
                        visible: isQuantityExceeded && !isAmountExceeded
                      },
                      cartError: isAmountExceeded,
                      showOutOfStock: false
                    }}
                    localisation={{
                      SUBTOTAL: t('RRP_BAG_TOTAL'),
                      TRADE_IN_MESSAGE: null,
                      VOUCHER_MESSAGE: null,
                      NOTIFY_MESSAGE: null,
                      ALERT_MESSAGE: quantityLimit.description,
                      ERROR_MESSAGE: amountLimit.description
                    }}
                  />
                )}
          </Loader>
        </div>
        {renderModal(modalType)}
      </>
    );
  }
};

/* istanbul ignore next */
const mapStateToProps = state => {
  const { rrpCart, fulfillment, bridge } = state;
  return {
    rrpOrder: rrpCart?.rrpOrder,
    addedItems: rrpCart?.addedItems,
    removedItems: rrpCart?.removedItems,
    rrpCart,
    mtposStock: fulfillment?.mtposStock?.data,
    mtposLoading: fulfillment?.mtposStock?.isLoading
  };
};

/* istanbul ignore next */
const mapDispatchToProps = dispatch => {
  const {
    retrieveRrpMasterOrderDetails,
    rrpAddMasterOrderItems,
    rrpRemoveMasterOrderItems,
    rrpCancelMasterOrderItems
  } = rrpCartActions;
  const { getShoppingCartData } = cartActions;
  return {
    updateRrpShoppingCartData: mockData =>
      dispatch({ type: 'RRP_SHOPPING_CART_SUCCESS', value: mockData }),
    retrieveRrpMasterOrderDetails: options =>
      dispatch(retrieveRrpMasterOrderDetails(options)),
    rrpAddMasterOrderItems: options =>
      dispatch(rrpAddMasterOrderItems(options)),
    rrpRemoveMasterOrderItems: options =>
      dispatch(rrpRemoveMasterOrderItems(options)),
    rrpCancelMasterOrderItems: options =>
      dispatch(rrpCancelMasterOrderItems(options)),
    getShoppingCartData: options => dispatch(getShoppingCartData(options)),
    updateRrpMasterOrderID: data =>
      dispatch({ type: ACTION_TYPES.RRP.SET_RRP_MASTER_ORDERID, data })
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(RRPShoppingCart);
